import { useCallback, useMemo, useRef } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { motion } from 'motion/react';
import { useAgentVisibility } from '@/entities/agent';
import { getCurrentOrganizationId, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { launchAgentWithWidget, startChatSession, useChatStore } from '@/features/chats';
import {
  getSpringboardTabByIdMerged,
  SmartStartGridV4,
  ToolGridV4,
  useSmartStartCards,
  useSpringboardTabs,
  type SmartStartCardItem,
} from '@/features/springboard';
import { FeaturedCard, type ToolsHubCategory } from '@/features/tools-hub';
import {
  SectionPageHeader,
  SectionPageHeaderBackButton,
  SectionPageHeaderContent,
  SectionPageHeaderDescription,
  SectionPageHeaderIcon,
  SectionPageHeaderTitle,
} from '@/shared/ui/layout/SectionPageHeader/SectionPageHeader';
import { ROUTES } from '@/shared/config/routes';
import { TOOL_CATEGORIES } from '@/shared/constants/category-colors';
import { eases } from '@/shared/constants/motion.constants';
import { useCurrentWorkspaceSlug } from '@/shared/hooks';
import { TOOL_BADGE_TINTS } from '@/shared/ui/ToolBadge';
import { FLOW_DIGEST_AGENT_ID } from '@/shared/utils/flow-digest';
import { useTranslation } from 'react-i18next';

const isValidCategory = (value: string): value is ToolsHubCategory =>
  (TOOL_CATEGORIES as readonly string[]).includes(value);

/**
 * Generic, data-driven page for /tools/:category. Today it is a dormant
 * fallback and renders for no real category: plan/create/scale/deliver each
 * have a dedicated static route (PlanPage/CreatePage/ScalePage/DeliverPage in
 * AppRoutes) that shadows this dynamic one, and any value outside
 * TOOL_CATEGORIES fails the guard below and redirects to /tools. It only takes
 * over when a category is added to TOOL_CATEGORIES + SPRINGBOARD_TABS without a
 * matching static route.
 *
 * The four static pages are ~85% identical to this component — they differ
 * only in their featured card (PlanPage also has a flag-gated handler) and a
 * GuidedLearningWorkflow section. Folding them into this page via per-category
 * config is a known consolidation opportunity if you are already in here.
 */
export const ToolsCategoryPage = () => {
  const { t } = useTranslation('tools');

  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Back returns to the surface the user came from (Tools, etc.), falling back
  // to Home when the page is reached directly (deep link, refresh, no origin).
  const backTo = (location.state as { from?: string } | null)?.from ?? ROUTES.HOME;
  const containerRef = useRef<HTMLDivElement>(null);

  const workspaceId = useCurrentWorkspaceId();
  const workspaceSlug = useCurrentWorkspaceSlug();
  const organizationId = getCurrentOrganizationId();
  const initializeChat = useChatStore((state) => state.initializeChat);

  const { isAgentEnabled } = useAgentVisibility([FLOW_DIGEST_AGENT_ID], {
    workspaceId,
    organizationId,
  });

  const { tabs: springboardTabs } = useSpringboardTabs();

  const selectedTab = useMemo(
    () => getSpringboardTabByIdMerged(springboardTabs, category),
    [springboardTabs, category],
  );

  const { canOpenTool } = usePermissions();

  const smartCardsResponse = useSmartStartCards({
    // eslint-disable-next-line i18next/no-literal-string -- springboard tab id
    tabId: category || 'plan',
    enabled: !!workspaceId,
    isAgentEnabled,
    canOpenAgent: canOpenTool,
  });

  const showFlowDigest =
    selectedTab?.id === 'plan' && isAgentEnabled(FLOW_DIGEST_AGENT_ID) && !!workspaceId;

  const launchAgent = useCallback(
    (agentId: string) => {
      launchAgentWithWidget(navigate, agentId);
    },
    [navigate],
  );

  const startSession = useCallback(
    async (item: SmartStartCardItem) => {
      await startChatSession(initializeChat, navigate, {
        agentId: item.input.agentId,
        prompt: item.input.prompt || '',
        inputs: item.input.options,
        visibilityScope: { workspaceId, organizationId },
      });
    },
    [initializeChat, navigate, workspaceId, organizationId],
  );

  if (!category || !isValidCategory(category) || !selectedTab) {
    return <Navigate to={ROUTES.TOOLS_HUB} replace />;
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div ref={containerRef} className="flex flex-col w-full gap-8 p-4">
        <SectionPageHeader>
          <SectionPageHeaderBackButton
            onClick={() => void navigate(backTo)}
            aria-label={t('toolsHub.toolsCategoryPage.back')}
          />
          <SectionPageHeaderIcon surfaceClassName={TOOL_BADGE_TINTS[selectedTab.id]}>
            <selectedTab.Icon className="size-5" />
          </SectionPageHeaderIcon>
          <SectionPageHeaderContent>
            <SectionPageHeaderTitle>{selectedTab.label}</SectionPageHeaderTitle>
            <SectionPageHeaderDescription>{selectedTab.subtitle}</SectionPageHeaderDescription>
          </SectionPageHeaderContent>
        </SectionPageHeader>

        <FeaturedCard
          id={`featured-${selectedTab.id}`}
          title={selectedTab.label}
          description={selectedTab.description}
          poster={selectedTab.poster}
          video={selectedTab.video}
          index={0}
        />

        <motion.div
          className="flex flex-col gap-6"
          initial={{ opacity: 0.025 }}
          animate={{ opacity: 1 }}
          transition={{ opacity: { duration: 0.5, delay: 0.5, ease: eases.sineOut } }}
        >
          <h3 className="text-lg font-medium">
            {t('toolsHub.toolsCategoryPage.toolsTo', { tabLabel: selectedTab.label })}
          </h3>
          <ToolGridV4
            selectedTab={selectedTab}
            showFlowDigest={showFlowDigest}
            workspaceSlug={workspaceSlug}
            onToolClick={launchAgent}
          />

          {!!smartCardsResponse.cards.length && (
            <>
              <h3 className="text-lg font-medium mt-2">
                {t('toolsHub.toolsCategoryPage.smartStarts')}
              </h3>
              <SmartStartGridV4
                smartCardsResponse={smartCardsResponse}
                tabId={selectedTab.id}
                onCardClick={(item) => void startSession(item)}
              />
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};
